/**
 * SubmissionsTimeline - Display submission history for a ticket
 * 
 * Shows all submission attempts with proof, status, and timestamps
 */

import { 
  CheckCircle, 
  Clock, 
  XCircle, 
  AlertCircle,
  ExternalLink,
  FileText,
  Mail,
  Globe,
  Send
} from 'lucide-react';
import type { Submission } from '../services/rpaService';

interface SubmissionsTimelineProps {
  submissions: Submission[];
}

export default function SubmissionsTimeline({ submissions }: SubmissionsTimelineProps) {
  if (submissions.length === 0) {
    return (
      <div className="bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-lg p-6 text-center">
        <Send className="w-12 h-12 text-slate-400 dark:text-slate-500 mx-auto mb-3" />
        <p className="text-sm text-slate-700 dark:text-slate-300">
          No submissions recorded yet
        </p>
      </div>
    );
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'accepted':
        return <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />;
      case 'rejected':
        return <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />;
      case 'acknowledged':
        return <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400" />;
      case 'submitted':
        return <Clock className="w-5 h-5 text-slate-600 dark:text-slate-300" />;
      default:
        return <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'accepted':
        return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-900 dark:text-green-100';
      case 'rejected':
        return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-900 dark:text-red-100';
      case 'acknowledged':
        return 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-900 dark:text-blue-100';
      case 'submitted':
        return 'bg-slate-50 dark:bg-slate-900/20 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-slate-100';
      default:
        return 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-100';
    }
  };

  const getMethodIcon = (method: string) => {
    switch (method) {
      case 'portal':
        return <Globe className="w-4 h-4" />;
      case 'pcnguard_email':
      case 'user_email':
        return <Mail className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  return (
    <div className="space-y-3">
      {submissions.map((submission) => (
        <div
          key={submission.id}
          className={`border rounded-lg p-4 ${getStatusColor(submission.status)} shadow-sm`}
        >
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-2">
              {getStatusIcon(submission.status)}
              <div>
                <p className="font-semibold text-sm">
                  {submission.submission_type === 'appeal' ? 'Appeal' : 
                   submission.submission_type === 'transfer' ? 'Transfer' : 
                   'Representation'} Submitted
                </p>
                <p className="text-xs text-current/90">
                  {new Date(submission.submitted_at).toLocaleString()}
                </p>
              </div>
            </div>
            <span className="text-xs font-semibold px-2 py-1 rounded border border-current/20 bg-white/70 dark:bg-slate-900/50">
              {submission.status}
            </span>
          </div>

          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2 text-xs">
              {getMethodIcon(submission.method)}
              <span className="text-current/90">
                via {submission.method === 'portal' ? 'Online Portal' : 
                     submission.method === 'pcnguard_email' ? 'PCNGuard Email' :
                     submission.method === 'user_email' ? 'User Email' :
                     submission.method}
              </span>
            </div>

            {submission.reference_number && (
              <div className="text-xs">
                <span className="text-current/90">Reference: </span>
                <span className="font-mono font-semibold">{submission.reference_number}</span>
              </div>
            )}

            {submission.proof_url && (
              <a
                href={submission.proof_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs font-semibold underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-current/50 rounded"
              >
                <ExternalLink className="w-3 h-3" />
                View Proof
              </a>
            )}

            {submission.notes && (
              <p className="text-xs text-current/90 mt-2 italic">
                {submission.notes}
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

